import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articleMeta } from "@/mocks/articles";
import { MAIN_SITE_URL } from "@/mocks/categories";
import { useSeo, getSiteUrl } from "@/hooks/useSeo";
import {
  useJsonLd,
  buildArticleSchema,
  buildFaqSchema,
  buildBreadcrumbSchema,
} from "@/lib/schema";

interface ArticleSection {
  h: string;
  p: string[];
}
interface ArticleTip {
  t: string;
  d: string;
}
interface ArticleFaq {
  q: string;
  a: string;
}
interface ArticleContent {
  title: string;
  subtitle: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  tag: string;
  readTime: string;
  intro: string[];
  sections: ArticleSection[];
  tips: ArticleTip[];
  faq: ArticleFaq[];
}

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const meta = articleMeta.find((m) => m.slug === slug);

  const raw = slug ? t(`article.content.${slug}`, { returnObjects: true }) : null;
  const content =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as unknown as ArticleContent)
      : null;

  useSeo(
    meta && content
      ? {
          title: content.metaTitle,
          description: content.metaDescription,
          keywords: content.metaKeywords,
          path: `/guides/${meta.slug}`,
          type: "article",
          image: meta.image,
        }
      : { title: t("article.notFound.title") },
  );

  useJsonLd(
    "schema-article",
    meta && content
      ? buildArticleSchema({
          headline: content.title,
          description: content.metaDescription,
          author: t("article.author"),
          dateModified: meta.date,
          url: `${getSiteUrl()}/guides/${meta.slug}`,
          image: meta.image,
        })
      : null,
  );

  useJsonLd(
    "schema-faq",
    meta && content
      ? buildFaqSchema(
          content.faq.map((item) => ({ q: item.q, a: item.a })),
        )
      : null,
  );

  useJsonLd(
    "schema-breadcrumb",
    meta && content
      ? buildBreadcrumbSchema([
          { name: t("article.breadcrumb.home"), path: `${getSiteUrl()}/` },
          { name: content.tag, path: `${getSiteUrl()}/guides/${meta.slug}` },
        ])
      : null,
  );

  if (!meta || !content) {
    return (
      <>
        <Navbar />
        <main className="mx-auto max-w-3xl px-4 py-24 text-center md:px-6">
          <h1 className="text-3xl font-bold text-foreground-950">
            {t("article.notFound.title")}
          </h1>
          <p className="mt-4 text-foreground-600">{t("article.notFound.text")}</p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            {t("article.notFound.back")}
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      <main>
        <article className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="text-sm text-foreground-500">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
                  {t("article.breadcrumb.home")}
                </Link>
              </li>
              <li aria-hidden="true">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li className="font-medium text-foreground-700">{content.tag}</li>
            </ol>
          </nav>

          {/* Header */}
          <header className="mt-6">
            <span className="inline-flex rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-700">
              {content.tag}
            </span>
            <h1 className="mt-4 text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {content.title}
            </h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-600 md:text-lg">
              {content.subtitle}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-6 text-sm text-foreground-500">
              <span className="flex items-center gap-2">
                <i className="ri-user-line text-primary-500" aria-hidden="true"></i>
                {t("article.author")}
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line text-primary-500" aria-hidden="true"></i>
                {content.readTime}
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-calendar-check-line text-primary-500" aria-hidden="true"></i>
                {meta.date}
              </span>
            </div>
          </header>

          {/* Intro */}
          <div className="mt-8 space-y-4 text-base leading-relaxed text-foreground-700">
            {content.intro.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {/* Body sections */}
          <div className="mt-10 space-y-10">
            {content.sections.map((section) => (
              <section key={section.h}>
                <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">
                  {section.h}
                </h2>
                <div className="mt-4 space-y-4 text-base leading-relaxed text-foreground-700">
                  {section.p.map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </section>
            ))}

            {/* Tips */}
            <section>
              <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">
                {t("article.tipsTitle")}
              </h2>
              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {content.tips.map((tip) => (
                  <div
                    key={tip.t}
                    className="rounded-xl border border-background-200 bg-white p-4"
                  >
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                        <i className="ri-check-line text-sm" aria-hidden="true"></i>
                      </span>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900">{tip.t}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-foreground-500">{tip.d}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section>
              <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">
                {t("article.faqTitle")}
              </h2>
              <div className="mt-5 space-y-3">
                {content.faq.map((item) => (
                  <div
                    key={item.q}
                    className="rounded-xl border border-background-200 bg-white p-5"
                  >
                    <h3 className="text-sm font-semibold text-foreground-900">{item.q}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.a}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* CTA */}
          <div className="mt-12 rounded-2xl bg-primary-600 p-6 text-center md:p-8">
            <h2 className="text-xl font-bold text-white md:text-2xl">
              {t("article.cta.title")}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-white/85">
              {t("article.cta.text")}
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-white px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100 cursor-pointer"
              >
                {t("article.cta.primary")}
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-white/40 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10 cursor-pointer"
              >
                {t("article.cta.secondary")}
                <i className="ri-arrow-right-up-line" aria-hidden="true"></i>
              </a>
            </div>
          </div>

          {/* Related resources (internal link to home) */}
          <div className="mt-10">
            <h2 className="text-base font-bold text-foreground-950">
              {t("article.related.title")}
            </h2>
            <Link
              to="/"
              className="mt-4 flex items-center gap-4 rounded-xl border border-background-200 bg-white p-4 transition-colors hover:border-primary-300 cursor-pointer"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className="ri-file-list-3-line text-xl" aria-hidden="true"></i>
              </span>
              <div>
                <h3 className="text-sm font-semibold text-foreground-900">
                  {t("article.related.homeCard")}
                </h3>
                <p className="mt-1 text-sm text-foreground-500">
                  {t("article.related.homeDesc")}
                </p>
              </div>
              <i
                className="ri-arrow-right-line ml-auto shrink-0 text-foreground-400"
                aria-hidden="true"
              ></i>
            </Link>
          </div>

          {/* Disclaimer */}
          <p className="mt-10 rounded-xl bg-background-100 px-5 py-4 text-xs leading-relaxed text-foreground-500">
            {t("article.disclaimer")}
          </p>
        </article>
      </main>

      <Footer />
    </>
  );
}